import React, { FC, useCallback, useEffect, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../../../common/toast/hooks/useToast'
import { ModuleRecord, ModuleRecordFormProps } from './ModuleRecordForm.d'
import { Card } from 'primereact/card'
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate'
import Common from '../../../common/Common'
import { Button, Alert, Row, Col, Form, Tab, Tabs } from 'react-bootstrap'
import { useAppSelector } from '../../../../store/hooks'
import PageHeader from '../../../layout/page/PageHeader'
import { Language } from '../../../../features/security/security.slice'
import { Formik, Form as FormikForm } from 'formik'
import * as Yup from 'yup'
import TextField from '../../../common/forms/fields/TextField'
import SelectField from '../../../common/forms/fields/SelectField'
import RecordStatusField from '../../../common/forms/fields/RecordStatusField'
import DeleteDialog from '../../../common/dialogs/DeleteDialog'
import RenderFieldsErrors from '../../../common/forms/errors/RenderFieldsErrors'
import { handleAxiosErrors } from '../../../common/forms/errors/Helper'
import RenderTranslatedFields from '../../../common/forms/fields/translated-fields/RenderTranslatedFields'
import FormActions from '../../../common/forms/FormActions'
import {
    getGroupTranslation,
    getTranslation,
} from '../../../common/utility/helpers'
import MediaFilesDialog from './MediaFilesDialog'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons'
import RenderFormikErrors from '../../../common/forms/errors/RenderFormikErrors'
import TextAreaField from '../../../common/forms/fields/TextAreaField'

const HEADER_FIELDS = [
    { key: 'main_header_top', label: 'Top Header' },
    { key: 'main_header_middle_big', label: 'Middle Header' },
    { key: 'main_header_bottom', label: 'Bottom Header' },
] as const

const ModuleRecordForm: FC<ModuleRecordFormProps> = ({
    module,
    id = null,
    inPopup = true,
    config = {},
    tempToken,
}) => {
    const isViewOrEdit = id !== null
    const theme = useAppSelector((state) => state.theme.theme)
    const initData = useAppSelector((state: any) => state.security.initData)

    const _module = initData.available_modules.filter((m) => m.code === module)

    const pageCode = isViewOrEdit
        ? `CLIENT_MODULE_${_module[0].id}_${id}`
        : 'TMPSET'

    const navigate = useNavigate()
    const axiosPrivate = useAxiosPrivate()

    const emptyModuleRecord: ModuleRecord = {
        title: '',
        main_header_top: null,
        main_header_middle_big: null,
        main_header_bottom: null,
        short_description: null,
        full_description: null,
        active: false,
    }

    const [pageDialog, setPageDialog] = useState(true)
    const [moduleRecord, setModuleRecord] =
        useState<ModuleRecord>(emptyModuleRecord)
    const [customColumns, setCustomColumns] = useState<[]>([])
    const [mediaFiles, setMediaFiles] = useState<[]>([])
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [errMsg, setErrMsg] = useState('')
    const [errorFields, setErrorFields] = useState<any>({})
    const [recordName, setRecordName] = useState('')

    let schema: Record<string, any> = {
        title: Yup.string().trim().required('Required'),
    }

    let _initialValues: Record<string, any> = {
        ...moduleRecord,
        title: pageCode + '_TITLE',
        main_header_top: pageCode + '_MAIN_HEADER_TOP',
        main_header_top_null: false,
        main_header_middle_big: pageCode + '_MAIN_HEADER_MIDDLE_BIG',
        main_header_middle_big_null: false,
        main_header_bottom: pageCode + '_MAIN_HEADER_BOTTOM',
        main_header_bottom_null: false,
        short_description: pageCode + '_SHORT_DESCRIPTION',
        full_description: pageCode + '_FULL_DESCRIPTION',
    }

    Object.values(initData.languages.data).map((lang: Language) => {
        schema[`title_${lang.id}`] = Yup.string()
            .trim()
            .required(`The Title (${lang.name}) field is required`)
        _initialValues[`title_${lang.id}`] = ''
        for (const hf of HEADER_FIELDS) {
            _initialValues[`${hf.key}_${lang.id}`] = ''
        }
        return true
    })

    const [initialValues, setInitialValues] = useState(_initialValues)

    const ModuleRecordFormSchema = Yup.object().shape(schema)

    const { triggerToast } = useToast()

    const loadPage = useCallback(async () => {
        setErrMsg('')
        setErrorFields({})
        setIsLoading(true)

        const routePath = isViewOrEdit
            ? '/client-modules/' + module + '/view/' + id
            : '/client-modules/' + module + '/new'

        try {
            const response = await axiosPrivate.get(routePath).finally(() => {
                setIsLoading(false)
            })
            if (response.data?.success === true) {
                if (response.data.data.record) {
                    const record = response.data.data.record
                    const translations = response.data.data.translations || {}
                    setModuleRecord(record)
                    setRecordName(
                        getTranslation(
                            translations,
                            record.title,
                            initData.languages.default.locale
                        )
                    )

                    const translationCodes: { key: string; code: string }[] = [
                        { key: 'title', code: record.title },
                        { key: 'short_description', code: record.short_description },
                        { key: 'full_description', code: record.full_description },
                    ]
                    for (const hf of HEADER_FIELDS) {
                        if (record[hf.key]) {
                            translationCodes.push({ key: hf.key, code: record[hf.key] })
                        }
                    }

                    const nextInitial: Record<string, unknown> = {
                        ...initialValues,
                        ...record,
                    }
                    for (const hf of HEADER_FIELDS) {
                        nextInitial[`${hf.key}_null`] = record[hf.key] === null
                    }

                    Object.keys(initData.languages.data).forEach((key) => {
                        const lang: Language = initData.languages.data[key] as Language
                        const locale = lang?.locale ?? key
                        const groupTranslations = getGroupTranslation(
                            translations,
                            translationCodes,
                            locale
                        )
                        nextInitial[`title_${lang.id}`] = groupTranslations['title']
                        nextInitial[`short_description_${lang.id}`] = groupTranslations['short_description']
                        nextInitial[`full_description_${lang.id}`] = groupTranslations['full_description']
                        for (const hf of HEADER_FIELDS) {
                            nextInitial[`${hf.key}_${lang.id}`] = groupTranslations[hf.key] || ''
                        }
                    })
                    setInitialValues(nextInitial as typeof initialValues)
                }

                setCustomColumns(response.data.data.custom_columns || [])
                setMediaFiles(response.data.data.media || [])
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err) {
            // TODO: Sort out error handling!
            if (!err?.response) {
                setErrMsg('No Server Response')
            } else if (err.response?.status === 404) {
                setErrMsg('Record was not found')
            } else {
                setErrMsg('Failed to load page')
            }
        }
    }, [axiosPrivate, id, module, initData.languages, isViewOrEdit])

    useEffect(() => {
        loadPage()
    }, [loadPage])

    const saveRecord = async (record) => {
        setErrMsg('')
        setIsSubmitting(true)

        const submitData = { ...record }
        for (const hf of HEADER_FIELDS) {
            if (submitData[`${hf.key}_null`]) {
                submitData[`${hf.key}_null`] = 'true'
            } else {
                delete submitData[`${hf.key}_null`]
            }
        }

        try {
            const routePath = isViewOrEdit
                ? `/client-modules/${module}/${id}?temp_token=${tempToken}`
                : `/client-modules/${module}/create?temp_token=${tempToken}`
            const response = await axiosPrivate
                .post(routePath, submitData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })
                .finally(() => {
                    setIsSubmitting(false)
                })

            if (response.data?.success === true) {
                triggerToast({
                    severity: 'success',
                    summary: config?.submitSuccessMsg?.header || 'Successful',
                    detail:
                        config?.submitSuccessMsg?.message ||
                        'Processed successfully',
                })
                hideDialog()
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err) {
            handleAxiosErrors(err, setErrMsg, setErrorFields, config)
        }
    }

    const hideDialog = () => {
        setPageDialog(false)
        navigate('/client/modules/' + module)
    }

    const formFooter = (
        <React.Fragment>
            <Button
                variant={theme === 'dark' ? 'dark' : 'light'}
                onClick={hideDialog}
            >
                <span className="small">Cancel</span>
            </Button>
            {isViewOrEdit && (
                <DeleteDialog
                    url={'/client-modules/' + module + '/delete/' + id}
                    record={moduleRecord}
                    deletedCallback={() => hideDialog()}
                    btnLabel="Delete"
                    showIcon={false}
                    message={
                        <>
                            <p className="mb-3 pb-3 border-bottom">
                                Are you sure you want to delete record:
                                <strong> {recordName}</strong>?
                            </p>
                        </>
                    }
                />
            )}
            <Button
                variant="success"
                disabled={isSubmitting || isLoading}
                // onClick={saveRecord}
                onClick={() => {
                    const formSubmitGtn = document.getElementById(
                        'moduleRecord-form-submit-btn'
                    )
                    formSubmitGtn.click()
                }}
            >
                <span className="small">
                    {isSubmitting ? (
                        <>
                            <FontAwesomeIcon icon={faCircleNotch} spin={true} />{' '}
                            {config?.saveButton?.savingLabel || 'Processing...'}
                        </>
                    ) : (
                        config?.saveButton?.label || 'Process'
                    )}
                </span>
            </Button>
        </React.Fragment>
    )

    const FormContent = (
        <>
            {isLoading && <Common.Loader text="Loading..." />}
            {errMsg && (
                <Alert show={true} variant="danger">
                    <Alert.Heading className="d-inline-block me-2">
                        Error!
                    </Alert.Heading>
                    <RenderFieldsErrors
                        errorFields={errorFields}
                        errMsg={errMsg}
                    />
                </Alert>
            )}
            <Formik
                initialValues={initialValues}
                validationSchema={ModuleRecordFormSchema}
                onSubmit={(values) => {
                    saveRecord(values)
                }}
                enableReinitialize={true}
            >
                {(formikProps) => {
                    return (
                        <FormikForm id="moduleRecord-form">
                            <RenderFormikErrors formikProps={formikProps} />
                            <Row>
                                <Col lg={12}>
                                    <RecordStatusField
                                        id="formActive"
                                        name="active"
                                        onLabel="De-Activate"
                                        offLabel="Activate"
                                        formikProps={formikProps}
                                    />
                                </Col>
                                <Col lg={7} xl={8}>
                                    <RenderTranslatedFields
                                        id="moduleRecordDescription"
                                        formikProps={formikProps}
                                        fields={[
                                            {
                                                id: 'formTitle',
                                                type: 'text',
                                                name: 'title',
                                                label: 'Title',
                                                required: true,
                                            },
                                            {
                                                id: 'formShortDescription',
                                                type: 'textarea',
                                                name: 'short_description',
                                                label: 'Short Description',
                                            },
                                            {
                                                id: 'formFullDescription',
                                                type: 'textarea',
                                                name: 'full_description',
                                                label: 'Full Description',
                                                enableEditor: true,
                                            },
                                        ]}
                                    />
                                </Col>
                                <Col lg={5} xl={4}>
                                    <fieldset>
                                        <legend>Media</legend>
                                        <MediaFilesDialog
                                            moduleCode={module}
                                            moduleId={id}
                                            tempToken={tempToken}
                                            label={
                                                mediaFiles.length + ' file(s)'
                                            }
                                            buttonLabel="Edit files"
                                            mediaFiles={mediaFiles}
                                            setMediaFiles={setMediaFiles}
                                        />
                                    </fieldset>
                                    <fieldset>
                                        <legend>Page Headers</legend>
                                        <Tabs
                                            id="headerFieldsTabs"
                                            className="mb-0"
                                        >
                                            {Object.keys(initData.languages.data).map((locale) => {
                                                const lang: Language = initData.languages.data[locale]
                                                return (
                                                    <Tab
                                                        eventKey={locale}
                                                        title={lang.name}
                                                        key={locale}
                                                        className="fade border-start border-end border-bottom mb-3 p-2 in-tab-content"
                                                    >
                                                        {HEADER_FIELDS.map((hf) => {
                                                            const isNull = !!formikProps.values[`${hf.key}_null`]
                                                            const valueProp = `${hf.key}_${lang.id}`
                                                            return (
                                                                <div key={hf.key} className="mb-3">
                                                                    <div className="d-flex align-items-center justify-content-between mb-1">
                                                                        <Form.Label className="mb-0 small fw-semibold">
                                                                            {hf.label}
                                                                        </Form.Label>
                                                                        <Form.Check
                                                                            type="checkbox"
                                                                            id={`${hf.key}_null_check_${lang.id}`}
                                                                            label="Blank"
                                                                            className="small"
                                                                            checked={isNull}
                                                                            onChange={(e) => {
                                                                                const nowChecked = e.target.checked
                                                                                formikProps.setFieldValue(`${hf.key}_null`, nowChecked)
                                                                                if (!nowChecked) {
                                                                                    Object.keys(initData.languages.data).forEach((k) => {
                                                                                        const lid = initData.languages.data[k].id
                                                                                        const fk = `${hf.key}_${lid}`
                                                                                        if (!formikProps.values[fk]) {
                                                                                            formikProps.setFieldValue(fk, initialValues[fk] || '')
                                                                                        }
                                                                                    })
                                                                                }
                                                                            }}
                                                                        />
                                                                    </div>
                                                                    <Form.Control
                                                                        type="text"
                                                                        size="sm"
                                                                        name={valueProp}
                                                                        value={formikProps.values[valueProp] ?? ''}
                                                                        disabled={isNull}
                                                                        onChange={formikProps.handleChange}
                                                                        onBlur={formikProps.handleBlur}
                                                                        placeholder={isNull ? '(blank)' : `Enter ${hf.label}`}
                                                                    />
                                                                </div>
                                                            )
                                                        })}
                                                    </Tab>
                                                )
                                            })}
                                        </Tabs>
                                    </fieldset>
                                    <fieldset>
                                        <legend>Custom Fields</legend>
                                        {customColumns.map((col: any) => {
                                            switch (col.type) {
                                                case 'integer':
                                                case 'number':
                                                case 'float':
                                                    return (
                                                        <TextField
                                                            key={col.name}
                                                            id={`formField${col.name}`}
                                                            type="number"
                                                            name={col.name}
                                                            label={col.label}
                                                            placeholder={`Enter ${col.label}`}
                                                            formikProps={
                                                                formikProps
                                                            }
                                                        />
                                                    )
                                                case 'select':
                                                    return (
                                                        <SelectField
                                                            key={col.name}
                                                            id={`formField${col.name}`}
                                                            name={col.name}
                                                            label={col.label}
                                                            formikProps={
                                                                formikProps
                                                            }
                                                            choices={
                                                                col.select_data
                                                            }
                                                        />
                                                    )
                                                case 'mediumText':
                                                case 'longText':
                                                    return (
                                                        <TextAreaField
                                                            key={col.name}
                                                            id={`formField${col.name}`}
                                                            name={col.name}
                                                            label={col.label}
                                                            placeholder={`Enter ${col.label}`}
                                                            formikProps={
                                                                formikProps
                                                            }
                                                        />
                                                    )
                                                case 'string':
                                                default:
                                                    return (
                                                        <TextField
                                                            key={col.name}
                                                            id={`formField${col.name}`}
                                                            name={col.name}
                                                            label={col.label}
                                                            placeholder={`Enter ${col.label}`}
                                                            formikProps={
                                                                formikProps
                                                            }
                                                        />
                                                    )
                                            }
                                        })}
                                    </fieldset>
                                </Col>
                            </Row>
                            <input
                                type="submit"
                                id="moduleRecord-form-submit-btn"
                                className="d-none"
                            />
                        </FormikForm>
                    )
                }}
            </Formik>
        </>
    )

    return (
        <>
            {inPopup ? (
                <Card className="flex justify-content-center">
                    <Dialog
                        visible={pageDialog}
                        breakpoints={{ '960px': '100vw', '640px': '100vw' }}
                        style={{ width: '75vw' }}
                        header={
                            isViewOrEdit
                                ? isLoading
                                    ? 'Loading...'
                                    : recordName
                                : 'Create new record'
                        }
                        modal
                        className="p-fluid"
                        footer={formFooter}
                        onHide={hideDialog}
                        maximizable
                        contentClassName={isLoading && 'wrapped-content'}
                        closeOnEscape={false}
                    >
                        {FormContent}
                    </Dialog>
                </Card>
            ) : (
                <>
                    <PageHeader
                        header={
                            isViewOrEdit
                                ? isLoading
                                    ? 'Loading...'
                                    : recordName
                                : 'Create new record'
                        }
                        buttons={[
                            {
                                variant: theme === 'dark' ? 'dark' : 'light',
                                label: 'Back to module',
                                icon: 'fa-solid fa-arrow-left',
                                url: '/client/modules/' + module,
                            },
                        ]}
                    />
                    {FormContent}
                    <FormActions formFooter={formFooter} />
                </>
            )}
        </>
    )
}

export default ModuleRecordForm
